// ============================================================
//  SatelliteMapEditor — Leaflet avec tuiles satellite Esri.
//  Vue réelle du parc, clic = pose balise avec lat/lng, drag
//  pour repositionner.
// ============================================================

import { useEffect, useRef } from "react";
import L from "leaflet";
import type { Balise } from "../lib/types";

// Centre par défaut : Parc Stalingrad Pantin (plus code VCR5+H8)
const DEFAULT_CENTER = { lat: 48.8914, lng: 2.4083 };
const DEFAULT_ZOOM = 18;

// Esri World Imagery : satellite gratuit sans clé, attribution requise
const ESRI_TILE_URL =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTR =
  "Tiles &copy; Esri — Maxar, GeoEye, Earthstar Geographics, CNES/Airbus DS, USDA, USGS";

export type SatelliteMapEditorProps = {
  balises: Balise[];
  selectedId: string | null;
  center?: { lat: number; lng: number };
  onCreate(coords: { lat: number; lng: number }): void;
  onMove(id: string, coords: { lat: number; lng: number }): void;
  onSelect(id: string | null): void;
};

export function SatelliteMapEditor({
  balises,
  selectedId,
  center = DEFAULT_CENTER,
  onCreate,
  onMove,
  onSelect,
}: SatelliteMapEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Map<string, L.Marker>>(new Map());

  // Init Leaflet
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([center.lat, center.lng], DEFAULT_ZOOM);
    mapRef.current = map;
    // Accès debug à l'instance (utile pour centrer/zoomer par script)
    (window as unknown as { __satMap?: L.Map }).__satMap = map;
    L.tileLayer(ESRI_TILE_URL, { maxZoom: 20, attribution: ESRI_ATTR }).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      onCreate({ lat: e.latlng.lat, lng: e.latlng.lng });
    });

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set<string>();
    for (const b of balises) {
      if (b.lat == null || b.lng == null) continue;
      seen.add(b.id);
      const isSelected = b.id === selectedId;
      let marker = markersRef.current.get(b.id);
      if (!marker) {
        marker = L.marker([b.lat, b.lng], {
          draggable: true,
          icon: makeIcon(isSelected, b.ordre_suggere),
        });
        marker.addTo(map);
        marker.on("click", () => onSelect(b.id));
        marker.on("dragend", () => {
          const ll = marker!.getLatLng();
          onMove(b.id, { lat: ll.lat, lng: ll.lng });
        });
        markersRef.current.set(b.id, marker);
      } else {
        marker.setLatLng([b.lat, b.lng]);
        marker.setIcon(makeIcon(isSelected, b.ordre_suggere));
      }
    }
    // Cleanup markers obsolètes
    for (const [id, m] of markersRef.current) {
      if (!seen.has(id)) {
        m.remove();
        markersRef.current.delete(id);
      }
    }
  }, [balises, selectedId, onMove, onSelect]);

  // Centre auto sur le premier marker quand on en pose un (si pas encore zoomé)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const withGps = balises.filter((b) => b.lat != null && b.lng != null);
    if (withGps.length === 1) {
      const b = withGps[0];
      map.setView([b.lat as number, b.lng as number], DEFAULT_ZOOM);
    }
  }, [balises.length]);

  return (
    <div className="map-editor">
      <div ref={containerRef} className="map-canvas" />
    </div>
  );
}

function makeIcon(selected: boolean, ordre: number | null): L.DivIcon {
  const label = ordre != null ? String(ordre) : "•";
  return L.divIcon({
    html: `<div class="balise-pin ${selected ? "selected" : ""}">${label}</div>`,
    className: "balise-pin-wrapper",
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}
